import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getBootstrapProject } from '@/api/endpoints/bootstrap'
import type {
  ActiveObjectResolution,
  BootstrapAccessError,
  BootstrapCapabilities,
  BootstrapMenuItem,
  BootstrapModule,
  BootstrapPreferences,
  BootstrapProject,
  BootstrapProjectResponse
} from '@/types/bootstrap'

export type ProjectContextStatus = 'idle' | 'loading' | 'ready' | 'error'

interface AxiosLikeError {
  response?: {
    status?: number
    data?: {
      reason?: string
      slug?: string
      projectId?: string
      projectName?: string
      publicId?: string
      publicIdType?: string
      // Task 3009 (US 2598): 423-kontraktens tilstand + fremdrift (ProjectNotReadyProblem).
      projectStatus?: string
      statusMessage?: string
      progressPercent?: number | null
    } | null
  }
}

function parseAccessError (err: unknown, slug: string, publicId?: string, publicIdType?: string): BootstrapAccessError {
  const candidate = err as AxiosLikeError | null
  const httpStatus = candidate?.response?.status ?? 0
  const reason = candidate?.response?.data?.reason ?? ''
  const data = candidate?.response?.data ?? null

  if (httpStatus === 404) {
    return { status: 404, reason: 'project-not-found', context: { slug } }
  }
  // Task 3009 (US 2598): 423 Locked — listet men ikke klart. Bær tilstand + melding
  // + fremdrift videre så «ikke klart ennå»-visningen kan vise dem.
  if (httpStatus === 423) {
    return {
      status: 423,
      reason: 'project-not-ready',
      context: {
        slug,
        projectId: data?.projectId,
        projectStatus: data?.projectStatus,
        statusMessage: data?.statusMessage,
        progressPercent: data?.progressPercent
      }
    }
  }
  if (httpStatus === 403 && reason === 'no-role') {
    return { status: 403, reason: 'no-role', context: { slug, projectId: data?.projectId } }
  }
  if (httpStatus === 403 && reason === 'project-archived') {
    return { status: 403, reason: 'project-archived', context: { slug, projectId: data?.projectId } }
  }
  if (httpStatus === 403 && reason === 'no-item-access') {
    return {
      status: 403,
      reason: 'no-item-access',
      context: {
        slug,
        projectName: data?.projectName ?? '',
        publicId: data?.publicId ?? publicId ?? '',
        publicIdType: data?.publicIdType ?? publicIdType ?? ''
      }
    }
  }
  return { status: httpStatus, reason: 'unknown', context: { slug } }
}

function cacheKey (s: string, publicId?: string): string {
  return publicId ? `${s}::${publicId}` : s
}

/**
 * Holder prosjekt-bootstrap-data (project + capabilities + modules + menu +
 * preferences + valgfri activeObject). Cache-er per `slug` + `publicId` slik
 * at in-app navigasjon mellom prosjekter krever ett refetch, mens samme route
 * reuser cached state.
 *
 * Tilgangsfeil (403/404 med strukturert reason) eksponeres via `accessError`
 * slik at router-guarden i #1223 kan rute presist (ProjectNotFound vs
 * ResourceAccessDenied) uten å gjette på HTTP-status.
 */
export const useProjectContextStore = defineStore('projectContext', () => {
  const status = ref<ProjectContextStatus>('idle')
  const slug = ref<string | null>(null)
  const currentProject = ref<BootstrapProject | null>(null)
  const capabilities = ref<BootstrapCapabilities | null>(null)
  const modules = ref<BootstrapModule[] | null>(null)
  const menu = ref<BootstrapMenuItem[] | null>(null)
  const preferences = ref<BootstrapPreferences | null>(null)
  const resolvedActiveObject = ref<ActiveObjectResolution | null>(null)
  // WI 2078: «finnes menyinnhold FØR tilgangsfiltrering» fra bootstrap —
  // skiller «ingen tilgang» fra «ikke satt opp» på velkomstsiden.
  const hasMenuContent = ref(false)
  const accessError = ref<BootstrapAccessError | null>(null)

  let inflightKey: string | null = null
  let inflight: Promise<void> | null = null

  function clearProjectState (): void {
    currentProject.value = null
    capabilities.value = null
    modules.value = null
    menu.value = null
    preferences.value = null
    resolvedActiveObject.value = null
    hasMenuContent.value = false
    accessError.value = null
  }

  async function ensureLoaded (nextSlug: string, publicId?: string, publicIdType?: string): Promise<void> {
    const requestedKey = cacheKey(nextSlug, publicId)

    if (status.value === 'ready' && slug.value === nextSlug) {
      // Samme slug: re-fetch kun hvis publicId-konteksten er endret. Uten
      // publicId i ny request reuser vi cached state (selv om vi har resolved
      // active object fra et tidligere kall).
      const currentResolvedPublicId = resolvedActiveObject.value?.publicId ?? null
      if (!publicId || currentResolvedPublicId === publicId) {
        return
      }
    }

    if (inflight !== null && inflightKey === requestedKey) {
      return inflight
    }

    slug.value = nextSlug
    if (status.value === 'ready' || status.value === 'error') {
      clearProjectState()
    }
    status.value = 'loading'
    inflightKey = requestedKey

    inflight = (async () => {
      try {
        const data: BootstrapProjectResponse = await getBootstrapProject(nextSlug, publicId, publicIdType)
        currentProject.value = data.project
        capabilities.value = data.capabilities
        modules.value = data.modules
        menu.value = data.menu
        preferences.value = data.preferences
        resolvedActiveObject.value = data.activeObject
        hasMenuContent.value = data.hasMenuContent
        accessError.value = null
        status.value = 'ready'
      } catch (err) {
        accessError.value = parseAccessError(err, nextSlug, publicId, publicIdType)
        status.value = 'error'
      } finally {
        inflight = null
        inflightKey = null
      }
    })()

    return inflight
  }

  function reset (): void {
    status.value = 'idle'
    slug.value = null
    clearProjectState()
    inflight = null
    inflightKey = null
  }

  return {
    status,
    slug,
    currentProject,
    capabilities,
    modules,
    menu,
    preferences,
    resolvedActiveObject,
    hasMenuContent,
    accessError,
    ensureLoaded,
    reset
  }
})
